import { lazy, LazyExoticComponent, Suspense, useEffect, useState } from "react";
import { useAuth } from "@/store/auth";
import * as Sentry from "@sentry/react";
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from "react-router-dom";

import { initializeDependencies } from "@/config/boot";
import PrivateRoute from "@/components/app/PrivateRoute";
import PublicRoute from "@/components/app/PublicRoute";
import { Spinner } from "@/components/shared/Spinner/Spinner";

import { RoutePermission } from "./components/app/RoutePermission";
import { Toaster } from "./components/ui/sonner";
import { subscriptionEnabled } from "./constants/subscriptions";

const DashboardLayout = lazy(() => import("./layouts/dashboard/DashboardLayout"));
const Account = lazy(() => import("@/pages/SupplierProfile"));
const SubscriptionPage = lazy(() => import("./pages/Subscription/SubscriptionPage"));
const CreditsPage = lazy(() => import("./pages/Credits/CreditsPage"));
const TransactionsPage = lazy(() => import("./pages/Credits/TransactionsPage"));
const CreditExtractPage = lazy(() => import("./pages/Credits/CreditExtractPage"));
const SegmentsPage = lazy(() => import("./pages/Segments/SegmentsPage"));
const OffersPage = lazy(() => import("./pages/Offers/Offers"));
const CreateOfferPage = lazy(() => import("./pages/Offers/CreateOffer"));
const ShowOfferPage = lazy(() => import("./pages/Offers/ShowOffer"));
const ClientShow = lazy(() => import("./pages/Clients/ClientShow"));
const SupplierEventsPage = lazy(() => import("./pages/Clients/SupplierEvents"));
const CoinsVaultPage = lazy(() => import("./pages/Profile/CoinsVault"));
const AcquisitionsListPage = lazy(() => import("./pages/Acquisitions/AcquisitionsList"));
const PurchasesOrderCreatePage = lazy(() => import("./pages/Purchases/PurchaseOrder"));
const PurchasesShowPage = lazy(() => import("./pages/Purchases/PurchasesShow"));
const QuotesListPage = lazy(() => import("./pages/Quotes/QuotesList"));
const QuoteCreationPage = lazy(() => import("./pages/Quotes/QuoteCreation"));
const PurchasesListPage = lazy(() => import("./pages/Purchases/PurchasesList"));
const PurchasesCreatePage = lazy(() => import("./pages/Purchases/PurchasesCreate"));
const InvoicePage = lazy(() => import("./pages/Invoices/InvoicePage"));
const TicketsList = lazy(() => import("./pages/Tickets/TicketsList"));
const TicketDetailPage = lazy(() => import("./pages/Tickets/TicketDetail"));
const TicketTypeCreatePage = lazy(() => import("./pages/Tickets/TicketTypeCreate"));
const TicketCreatePage = lazy(() => import("./pages/Tickets/TicketCreate"));
const IntegrationsPage = lazy(() => import("./pages/Integrations/IntegrationsPage"));
const IntegrationsCallbackPage = lazy(
  () => import("./pages/Integrations/IntegrationsCallbackPage")
);

const PaymentDetailPage = lazy(() => import("./pages/Payments/PaymentDetailPage"));
const CommercialsList = lazy(() => import("./pages/Commercials/CommercialsList"));
const BulkLoadsPage = lazy(() => import("./pages/bulk-loads/BulkLoadsPage"));
const NewBulkLoadPage = lazy(() => import("./pages/bulk-loads/NewBulkLoadPage"));
const BulkLoadPage = lazy(() => import("./pages/bulk-loads/BulkLoadPage"));
const RoutesList = lazy(() => import("./pages/Routes/RoutesList"));
const EditRoute = lazy(() => import("./pages/Routes/EditRoute"));
const CreateRoute = lazy(() => import("./pages/Routes/CreateRoute"));
const Restricted = lazy(() => import("./pages/Restricted/Restricted"));
const NotFoundPage = lazy(() => import("./pages/NotFound"));
const EmailLinkLogin = lazy(() => import("./pages/EmailLinkLogin"));
const Login = lazy(() => import("./pages/Login/Login"));
const SignupPage = lazy(() => import("./pages/Signup/SignupPage"));

const ClientsPage = lazy(() => import("./pages/Clients/ClientsPage"));
const CategoriesPage = lazy(() => import("@/pages/Products/Categories"));
const BrandsPage = lazy(() => import("@/pages/Products/Brands"));
const PaymentsPage = lazy(() => import("@/pages/Payments/Payments"));
const CreatePayment = lazy(() => import("./pages/Payments/CreatePayment"));
const PaymentsDetailPage = lazy(() => import("./pages/Payments/PaymentsDetailPage"));
const PaymentEditPage = lazy(() => import("./pages/Payments/PaymentEditPage"));

type LazyComponent = LazyExoticComponent<() => JSX.Element | null>;

function App() {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    initializeDependencies()
      .then(() => setReady(true))
      .catch(() => setError("Error de configuración remota."));
  }, []);

  useEffect(() => {
    if (user?.id) {
      Sentry.onLoad(() => {
        Sentry.setUser({
          id: user?.id ?? "",
          email: user?.email ?? "",
        });
      });
    } else {
      Sentry.onLoad(() => {
        Sentry.setUser(null);
      });
    }
  }, [user]);

  if (error) return <>{error}</>;
  if (!ready) return <Spinner />;

  const lazyRoute = (Component: LazyComponent, permissions: string[] = []) => (
    <RoutePermission
      permissions={permissions}
      element={
        <Suspense fallback={<Spinner />}>
          <Component />
        </Suspense>
      }
    />
  );

  return (
    <>
      <Toaster richColors position="top-right" />
      <Router>
        <Routes>
          <Route path="/" element={<PrivateRoute />}>
            <Route
              path="/"
              element={
                <DashboardLayout>
                  <Outlet />
                </DashboardLayout>
              }
            >
              <Route path="/" element={<Navigate replace to="purchases" />} />
              <Route path="/account" element={lazyRoute(Account, ["route.account.show"])} />
              <Route
                path="/tania/documents"
                element={lazyRoute(lazy(() => import("./pages/TaniaDocuments/DocumentsPage")))}
              />
              <Route
                path="/tania/conversations"
                element={lazyRoute(
                  lazy(() => import("./pages/TaniaConversations/TaniaConversationsPage"))
                )}
              />
              <Route
                path="/tania/metrics"
                element={lazyRoute(lazy(() => import("./pages/Metrics/TaniaMetricsPage")))}
              />
              {subscriptionEnabled && (
                <Route
                  path="/subscription"
                  element={lazyRoute(SubscriptionPage, ["route.subscription.show"])}
                />
              )}
              <Route
                path="/customize"
                element={lazyRoute(lazy(() => import("./pages/Customize/CustomizePage")))}
              />
              <Route
                path="/metrics"
                element={lazyRoute(lazy(() => import("./pages/PowerBIReport")))}
              />
              <Route
                path="/metrics/dashboard"
                element={lazyRoute(lazy(() => import("./pages/Metrics/DistrifyMetricsPage")))}
              />

              <Route
                path="/dashboard"
                element={lazyRoute(
                  lazy(() => import("./pages/Dashboard")),
                  ["route.dashboard.show"]
                )}
              />
              <Route
                path="/commercial-tracking"
                element={lazyRoute(
                  lazy(() => import("./pages/Commercials/CommercialsTracking")),
                  ["route.commercial-tracking.show"]
                )}
              />
              <Route
                path="/help"
                element={lazyRoute(
                  lazy(() => import("./pages/Help/Help")),
                  ["route.help.show"]
                )}
              />
              <Route
                path="/orders"
                element={lazyRoute(
                  lazy(() => import("./pages/Orders/Orders")),
                  ["route.orders.list"]
                )}
              />
              <Route
                path="/orders/:id"
                element={lazyRoute(
                  lazy(() => import("./pages/Orders/OrderDetail")),
                  ["route.orders.show"]
                )}
              />
              <Route
                path="/orders/:id/invoice"
                element={lazyRoute(
                  lazy(() => import("./pages/Invoices/InvoiceOrderPage")),
                  ["route.orders.show"]
                )}
              />
              <Route
                path="/products"
                element={lazyRoute(
                  lazy(() => import("./pages/Products/ProductsPage")),
                  ["route.products.list"]
                )}
              />
              <Route
                path="/products/new"
                element={lazyRoute(
                  lazy(() => import("./pages/ProductManagement/NewProductPage")),
                  ["route.products.create"]
                )}
              />
              <Route
                path="/products/:id"
                element={lazyRoute(
                  lazy(() => import("./pages/ProductManagement/ManageProductPage")),
                  ["route.products.detail"]
                )}
              />
              <Route
                path="/products/create/new"
                element={lazyRoute(
                  lazy(() => import("./pages/Products/CreateProductVariant")),
                  ["route.products.create"]
                )}
              />
              <Route
                path="/products/:id/old"
                element={lazyRoute(
                  lazy(() => import("./pages/Products/VariantDetailPage")),
                  ["route.products.detail"]
                )}
              />
              <Route
                path="/products/:id/edit"
                element={lazyRoute(
                  lazy(() => import("./pages/Products/EditProduct")),
                  ["action.product.edit"]
                )}
              />
              <Route
                path="/curves"
                element={lazyRoute(
                  lazy(() => import("./pages/Products/CurvesPage")),
                  ["route.products.list"]
                )}
              />
              <Route
                path="/splits"
                element={lazyRoute(
                  lazy(() => import("./pages/Products/Splits")),
                  ["route.products.list"]
                )}
              />
              <Route
                path="/categories"
                element={lazyRoute(CategoriesPage, ["route.categories.list"])}
              />
              <Route path="/brands" element={lazyRoute(BrandsPage, ["route.brands.list"])} />
              <Route path="/payments" element={lazyRoute(PaymentsPage, ["route.payments.list"])} />
              <Route
                path="/payments/create/new"
                element={lazyRoute(CreatePayment, ["route.payments.create"])}
              />
              <Route
                path="/payments/:id/edit"
                element={lazyRoute(PaymentEditPage, ["route.payments.create"])}
              />
              <Route
                path="/payment/:id"
                element={lazyRoute(PaymentDetailPage, ["route.payments.show"])}
              />
              <Route path="/clients" element={lazyRoute(ClientsPage, ["route.clients.list"])} />
              <Route path="/credits" element={lazyRoute(CreditsPage, ["route.credits.list"])} />
              <Route
                path="/credits/:id/transactions"
                element={lazyRoute(TransactionsPage, ["route.transactions.list"])}
              />
              <Route
                path="/credits/:id/transactions/extract"
                element={lazyRoute(CreditExtractPage, ["route.transactions.list"])}
              />
              <Route path="/segments" element={lazyRoute(SegmentsPage, ["route.clients.list"])} />
              <Route path="/offers" element={lazyRoute(OffersPage, ["route.offers.list"])} />
              <Route
                path="/offers/create"
                element={lazyRoute(CreateOfferPage, ["route.offers.create"])}
              />
              <Route
                path="/offers/:id/show"
                element={lazyRoute(ShowOfferPage, ["route.offers.show"])}
              />
              <Route
                path="/clients/:id/show"
                element={lazyRoute(ClientShow, ["route.clients.show"])}
              />
              <Route
                path="/clients/events"
                element={lazyRoute(SupplierEventsPage, ["route.clients.show"])}
              />
              <Route
                path="/clients/:id/coins"
                element={lazyRoute(CoinsVaultPage, ["route.clients.show"])}
              />
              <Route
                path="/acquisitions"
                element={lazyRoute(AcquisitionsListPage, ["route.purchases.my-purchases"])}
              />
              <Route
                path="/acquisitions/create"
                element={lazyRoute(PurchasesOrderCreatePage, ["route.purchases.my-purchases"])}
              />
              <Route
                path="/acquisitions/:id/show"
                element={lazyRoute(PurchasesShowPage, ["route.purchases.my-purchases"])}
              />
              <Route path="/quotes" element={lazyRoute(QuotesListPage, ["route.purchases.list"])} />
              <Route
                path="/quotes/create"
                element={lazyRoute(QuoteCreationPage, ["route.purchases.create"])}
              />
              <Route
                path="/quotes/:id/show"
                element={lazyRoute(PurchasesShowPage, ["route.purchases.show"])}
              />
              <Route
                path="/purchases"
                element={lazyRoute(PurchasesListPage, ["route.purchases.list"])}
              />
              <Route
                path="/purchases/create"
                element={lazyRoute(PurchasesCreatePage, ["route.purchases.create"])}
              />
              <Route
                path="/purchases/:id/show"
                element={lazyRoute(PurchasesShowPage, ["route.purchases.show"])}
              />
              <Route
                path="/purchases/:id/invoice"
                element={lazyRoute(InvoicePage, ["route.purchases.show"])}
              />
              <Route path="/tickets" element={lazyRoute(TicketsList, ["route.tickets.list"])} />
              <Route
                path="/tickets/:id"
                element={lazyRoute(TicketDetailPage, ["route.tickets.list"])}
              />
              <Route
                path="/tickets/create/:type"
                element={lazyRoute(TicketTypeCreatePage, ["route.tickets.list"])}
              />
              <Route
                path="/tickets/create/:type/:purchaseId"
                element={lazyRoute(TicketCreatePage, ["route.tickets.list"])}
              />
              <Route
                path="/integrations"
                element={lazyRoute(IntegrationsPage, ["route.integrations"])}
              />
              <Route
                path="/integrations/:id/callback"
                element={lazyRoute(IntegrationsCallbackPage)}
              />
              <Route
                path="/payments/:id"
                element={lazyRoute(PaymentsDetailPage, ["route.payments.show"])}
              />
              <Route
                path="/commercials"
                element={lazyRoute(CommercialsList, ["route.commercials.list"])}
              />
              <Route
                path="/bulk-loads"
                element={lazyRoute(BulkLoadsPage, ["route.bulk-loads.list"])}
              />
              <Route
                path="/bulk-loads/new"
                element={lazyRoute(NewBulkLoadPage, ["route.bulk-loads.create"])}
              />
              <Route
                path="/bulk-loads/:id"
                element={lazyRoute(BulkLoadPage, ["route.bulk-loads.show"])}
              />
              <Route
                path="/dispatch-routes/new"
                element={lazyRoute(
                  lazy(() => import("./pages/DispatchRoutes/NewDispatchRoutePage"))
                )}
              />
              <Route path="/routes" element={lazyRoute(RoutesList, [])} />
              <Route path="/routes/:id" element={lazyRoute(EditRoute, [])} />
              <Route path="/routes/create" element={lazyRoute(CreateRoute, [])} />
              <Route path="/restricted" element={<Restricted />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
          <Route path="/" element={<PublicRoute />}>
            <Route path="/link-login" element={lazyRoute(EmailLinkLogin)} />
            <Route path="/login" element={lazyRoute(Login)} />
            <Route path="/signup" element={lazyRoute(SignupPage)} />
          </Route>
        </Routes>
      </Router>
    </>
  );
}

export default App;
